import { useNavigate } from 'react-router-dom'
import { Text, Accordion, PrimaryButton, useKeycloakSession, getKeycloak } from '@artprov/design-system'

/**
 * @fileoverview Contenido de la Página Pública
 *
 * Esta página demuestra cómo declarar una ruta PÚBLICA en organismos públicos.
 *
 * ¿Qué es un componente público?
 * - Es una pantalla accesible sin autenticación de Keycloak.
 * - Se declara como <Route> directo dentro de <AppRouter>, FUERA del
 *   wrapper <AuthKeycloak>. Por estar fuera del guard, no exige sesión
 *   ni rol y se renderiza para cualquier visitante.
 *
 * ¿Cómo funciona?
 * - <AppRouter> soporta rutas públicas y protegidas en el mismo árbol.
 *   Las protegidas van dentro de <AuthKeycloak>, las públicas quedan
 *   afuera.
 * - <AppRouter> dispara internamente checkKeycloakSession en modo
 *   silencioso para detectar si el usuario YA tiene sesión activa.
 *   Si la tiene, el header muestra el dropdown de MiPerfil; si no,
 *   sólo se ve la página pública.
 */
export default function Contenido() {
  const navigate = useNavigate()
  const isAuthenticated = useKeycloakSession()

  const handleLogin = () => {
    const keycloak = getKeycloak()
    keycloak?.login({ redirectUri: globalThis.location.origin })
  }

  const handleGoPrivate = () => {
    navigate('/')
  }

  return (
    <div className="h-full flex flex-col gap-y-4 items-center justify-center my-4 px-4 w-full">
      <Text variant="h5" className="text-gray-600 mb-2">
        Página Pública de Ejemplo
      </Text>

      <div className="w-full max-w-4xl flex flex-col gap-4">
        <div className="border border-gray-200 rounded-lg p-4 flex flex-row items-center justify-between gap-3 bg-white">
          <Text variant="h6" className="text-gray-700">
            Estado de sesión en vivo
          </Text>

          <Text
            variant="body1"
            className={isAuthenticated ? 'text-green-600 font-semibold' : 'text-gray-500 font-semibold'}
          >
            {isAuthenticated ? '● Sesión activa detectada' : '○ Sin sesión activa'}
          </Text>

          {isAuthenticated ? (
            <PrimaryButton onClick={handleGoPrivate} size="small">
              Ir al área privada
            </PrimaryButton>
          ) : (
            <PrimaryButton onClick={handleLogin} size="small">
              Iniciar sesión
            </PrimaryButton>
          )}
        </div>

        <Accordion
          title="¿Qué es un componente público?"
          subtitle="Pantalla accesible sin autenticación"
          isVisibleSubtitle={true}
          isVisibleBadge={false}
          isOpenState={true}
        >
          <Text variant="body1" className="text-gray-700">
            Un componente público es una pantalla que el usuario puede ver sin necesidad de iniciar sesión en Keycloak.
            Se declara como <code>{'<Route>'}</code> directo dentro de <code>{'<AppRouter>'}</code>, fuera del wrapper{' '}
            <code>{'<AuthKeycloak>'}</code>.
          </Text>
        </Accordion>

        <Accordion
          title="¿Cómo funciona?"
          subtitle="Rutas públicas + protegidas"
          isVisibleSubtitle={true}
          isVisibleBadge={false}
          isOpenState={false}
        >
          <Text variant="body1" className="text-gray-700">
            <code>{'<AppRouter>'}</code> permite mezclar rutas públicas y protegidas en el mismo árbol. Las protegidas
            se envuelven en <code>{'<AuthKeycloak>'}</code> y heredan el guard de autenticación/rol; las públicas quedan
            fuera y se renderizan para cualquier visitante. Si el usuario ya está logueado, el header muestra el
            dropdown de MiPerfil; si no, sólo se ve esta página.
          </Text>
        </Accordion>
      </div>
    </div>
  )
}
